import { useContext } from 'react';

import { type IServerSideDatasource } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { PrimaryContext } from '@/services';

import { auditLogColumnDefs } from '../util/grid';
import { type TransformedLog } from '../util/types';
import { ChangesPanel } from './ChangesPanel';

const CACHE_BLOCK_SIZE = 50;
const PAGE_SIZE = 50;
const LOAD_DEBOUNCE_MS = 250;

function NoHistory() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2">
      <div className="text-2xl font-semibold text-gray-700 dark:text-gray-300">
        No Audit History Found
      </div>
      <div className="text-center text-gray-500 dark:text-gray-400">
        There are no audit history available for the selected entity. This could
        be because no actions have been performed on it yet, or the provided ID
        is incorrect.
      </div>
    </div>
  );
}

type GridProps = {
  grid: React.RefObject<AgGridReact<TransformedLog>>;
  dataSource: IServerSideDatasource;
};

export function Grid({ grid, dataSource }: GridProps) {
  const { themeMode } = useContext(PrimaryContext);
  const defaultGridOptions = useDefaultGridOptions();

  return (
    <AgGridReact<TransformedLog>
      gridOptions={defaultGridOptions}
      ref={grid}
      className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full`}
      columnDefs={auditLogColumnDefs}
      rowSelection="single"
      suppressRowClickSelection={false}
      defaultColDef={defaultColDef}
      masterDetail
      detailRowAutoHeight
      detailCellRenderer={ChangesPanel}
      rowModelType="serverSide"
      serverSideDatasource={dataSource}
      cacheBlockSize={CACHE_BLOCK_SIZE}
      blockLoadDebounceMillis={LOAD_DEBOUNCE_MS}
      pagination
      paginationPageSize={PAGE_SIZE}
      getRowId={(row) => row.data.id}
      onRowClicked={(params) => params.node.setExpanded(!params.node.expanded)}
      onFirstDataRendered={(params) => {
        params.api.forEachNode((node) => {
          node.setExpanded(true);
        });
      }}
      onRowDataUpdated={(params) => {
        params.api.forEachNode((node) => {
          if (!node.expanded) {
            node.setExpanded(true);
          }
        });
      }}
      noRowsOverlayComponent={NoHistory}
    />
  );
}
